import { getResourcesFree } from 'entities/hallmap';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import {
	selectOrderBookingData,
	selectOrderDetail,
	useOrderState,
} from 'entities/orders';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { getOrdersByDate, getOrdersNext } from 'entities/orders/model/ordersThunk';
import { useGetSlotsByDate } from 'features/hallmapToolbar/datePicker/lib/useGetSlotsByDate';
import { usePaginationFilter } from 'shared/lib/pagination';
import { useLocation } from 'react-router-dom';
import { selectSelectedTimeslot, useTimeslot, useTimeslotZone } from 'entities/toolbar/timeslots';
import { isToday } from 'date-fns';

interface UseBookingEventsParams {
	toProhibitClose: (isClose?: boolean) => void;
	enableBooking: () => void;
	successClose: (force?: boolean) => void;
	setBookingError: (errorMessage: string) => void;
}

export const useBookingEvents = ({
	toProhibitClose,
	enableBooking,
	successClose,
	setBookingError,
}: UseBookingEventsParams) => {
	const bookingData = useAppSelector(selectOrderBookingData);
	const order = useAppSelector(selectOrderDetail);
	const { isWaitingList } = useOrderState(order);
	const paginationFilter = usePaginationFilter();
	const selectedTimeslot = useAppSelector(selectSelectedTimeslot);
	const { formatISODateBySlotZone } = useTimeslotZone();
	const { pathname } = useLocation();
	const dispatch = useAppDispatch();
	const { toTimeslot } = useTimeslot();
	const [prohibitBookingAgain, setProhibitBookingAgain] = useState(false);
	const { getSlotsByDate } = useGetSlotsByDate();

	const removeRestrictions = useCallback(() => {
		toProhibitClose(false);
		enableBooking();
	}, [toProhibitClose, enableBooking]);

	const switchTimelineAfterBooking = useCallback(() => {
		const bookingDate = new Date(bookingData?.dt_start);
		toTimeslot(bookingData?.dt_start, { hours: bookingDate.getHours()?.toString() });
		getSlotsByDate(bookingDate);
	}, [getSlotsByDate, bookingData, toTimeslot]);

	const loadOrdersByDate = useCallback((date: Date) => {
		const dateStart = new Date(date);
		dateStart.setHours(0, 0, 0, 0);
		const dateEnd = new Date(date);
		dateEnd.setHours(23, 59, 59, 59);

		dispatch(getOrdersByDate({
			dt_start__gte: formatISODateBySlotZone(dateStart),
			dt_start__lte: formatISODateBySlotZone(dateEnd),
			status: 'appointed'
		}))
	}, [dispatch, formatISODateBySlotZone])

	const onSuccessBookingActions = useCallback(() => {
		toast.success(
			bookingData.id && !isWaitingList
				? `Изменения в бронировании на имя ${bookingData?.person?.name} сохранены!`
				: `Бронирование на имя ${bookingData?.person?.name} успешно создано!`,
		);

		localStorage.removeItem('booking');
		localStorage.removeItem('favoriteSavedTime');

		if (pathname === '/waiting-list' || pathname === '/bookings') {
			paginationFilter?.fetchDataWithDispatch();
		}
		if (isToday(selectedTimeslot)) {
			dispatch(getOrdersNext({}));
		} else {
			loadOrdersByDate(new Date(selectedTimeslot));
		}
		switchTimelineAfterBooking();
		successClose(true);
	}, [
		loadOrdersByDate,
		selectedTimeslot,
		isWaitingList,
		bookingData,
		successClose,
		dispatch,
		paginationFilter,
		switchTimelineAfterBooking,
		pathname,
	]);

	const onErrorBookingActions = useCallback(
		(errors: string) => {
			removeRestrictions();
			setBookingError(
				`К сожалению, не удалось создать бронирование по следующей причине: "${errors}". Попробуйте, пожалуйста, изменить время, продолжительность или забронированные ресурсы!`,
			);

			dispatch(
				getResourcesFree({
					duration: bookingData.duration,
					dt_requested_start: bookingData.dt_start,
					exclude_appointment: order?.appointment?.id || undefined
				}),
			);

			setProhibitBookingAgain(false);
		},
		[bookingData, dispatch, removeRestrictions, setBookingError, order],
	);

	return {
		removeRestrictions,
		onSuccessBookingActions,
		onErrorBookingActions,
		prohibitBookingAgain,
	};
};
